import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format, isPast, addMinutes } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { 
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Users,
  Building,
  Video,
  MessageSquare,
  Link as LinkIcon,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface Interview {
  id: string;
  date: string;
  time: string;
  duration: string;
  status: string;
  location: string;
  type: string;
  title: string;
  description?: string;
  participants: string[];
  profile_id?: string;
  application_id?: string;
  job?: {
    id: string;
    title: string;
    department: string;
    profiles: {
      id: string;
      full_name: string;
      company_name: string;
    };
  };
  feedback?: Array<{
    id: string;
    notes: string;
    overall_rating: number;
    created_at: string;
    evaluator: {
      id: string;
      full_name: string;
    };
  }>;
}

export default function InterviewDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [interview, setInterview] = useState<Interview | null>(null);
  const [loading, setLoading] = useState(true);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  const [feedback, setFeedback] = useState({
    notes: '',
    rating: 0,
    recommendation: ''
  });

  useEffect(() => {
    if (!user || !id) return;
    fetchInterviewDetails();
  }, [user, id]);

  const fetchInterviewDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('interviews')
        .select(`
          *,
          job:recruiting_projects (
            id,
            title,
            department,
            profiles (
              id,
              full_name,
              company_name
            )
          ),
          feedback:candidate_evaluations (
            id,
            notes,
            overall_rating,
            created_at,
            evaluator:profiles (
              id,
              full_name
            )
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setInterview(data);

      // Check if feedback is needed
      if (isInterviewPast(data) && !data.feedback?.length && isInterviewHost(data)) {
        setFeedbackDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching interview:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load interview details'
      });
      navigate('/loops');
    } finally {
      setLoading(false);
    }
  };

  const isInterviewPast = (interview: Interview | null) => {
    if (!interview?.date || !interview?.time) return false;
    const interviewDateTime = new Date(`${interview.date}T${interview.time}`);
    const endTime = addMinutes(interviewDateTime, parseInt(interview.duration) || 60);
    return isPast(endTime);
  };

  const isInterviewHost = (interview: Interview | null) => {
    return user?.id === interview?.profile_id;
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      const { error } = await supabase
        .from('interviews')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Interview ${newStatus}`
      });

      fetchInterviewDetails();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update interview status'
      });
    }
  };

  const handleReschedule = async (newDate: string, newTime: string) => {
    setIsRescheduling(true);
    try {
      const { error } = await supabase
        .from('interviews')
        .update({ 
          date: newDate,
          time: newTime,
          status: 'scheduled',
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Interview rescheduled successfully'
      });

      setRescheduleDialogOpen(false);
      fetchInterviewDetails();
    } catch (error) {
      console.error('Error rescheduling interview:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to reschedule interview'
      });
    } finally {
      setIsRescheduling(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!user || !interview) return;
    
    setIsSavingFeedback(true);
    try {
      const { error } = await supabase
        .from('candidate_evaluations')
        .insert([{
          profile_id: interview.participants[0], // Assuming first participant is the candidate
          evaluator_id: user.id,
          application_id: interview.application_id,
          notes: feedback.notes,
          overall_rating: feedback.rating,
          status: 'completed',
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Interview feedback submitted successfully'
      });

      setFeedbackDialogOpen(false);
      fetchInterviewDetails();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to submit feedback'
      });
    } finally {
      setIsSavingFeedback(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <div className="flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!interview) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Interview Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The requested interview could not be found.
          </p>
          <Button onClick={() => navigate('/loops')}>
            Return to Loops
          </Button>
        </div>
      </div>
    );
  }

  const isPast = isInterviewPast(interview);
  const needsFeedback = isPast && !interview.feedback?.length && isInterviewHost(interview);

  return (
    <div className="container py-8 px-4 md:px-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/loops')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{interview.title}</h1>
            <p className="text-muted-foreground">
              {interview.job?.title} at {interview.job?.profiles?.company_name}
            </p>
          </div>
        </div>

        {/* Feedback Alert */}
        {needsFeedback && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Feedback Required</AlertTitle>
            <AlertDescription>
              This interview has ended. Please provide your feedback about the candidate.
            </AlertDescription>
            <Button 
              className="mt-2" 
              onClick={() => setFeedbackDialogOpen(true)}
            >
              <FileText className="h-4 w-4 mr-2" />
              Submit Feedback
            </Button>
          </Alert>
        )}

        {/* Interview Details Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Interview Details</CardTitle>
              <Badge variant={
                interview.status === 'confirmed' ? 'default' :
                interview.status === 'cancelled' ? 'destructive' :
                'secondary'
              }>
                {interview.status.charAt(0).toUpperCase() + interview.status.slice(1)}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>Date: {format(new Date(interview.date), 'MMMM d, yyyy')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>
                    Time: {format(new Date(`${interview.date}T${interview.time}`), 'h:mm a')} {' '}
                    ({interview.duration})
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>Location: {interview.location}</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>Participants:</span>
                </div>
                <div className="pl-6">
                  {interview.participants.map((participant: string, index: number) => (
                    <div key={index} className="text-sm">{participant}</div>
                  ))}
                </div>
              </div>
            </div>

            {interview.description && (
              <div className="pt-4 border-t">
                <h3 className="font-medium mb-2">Description</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-line">
                  {interview.description}
                </p>
              </div>
            )}

            {/* Interview Feedback */}
            {interview.feedback?.length > 0 && (
              <div className="pt-4 border-t">
                <h3 className="font-medium mb-2">Feedback</h3>
                <div className="space-y-4">
                  {interview.feedback.map((feedback: any) => (
                    <div key={feedback.id} className="bg-muted/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{feedback.evaluator.full_name}</span>
                          <span className="text-sm text-muted-foreground">
                            {format(new Date(feedback.created_at), 'MMM d, yyyy')}
                          </span>
                        </div>
                        <Badge variant="outline">Rating: {feedback.overall_rating}/10</Badge>
                      </div>
                      {feedback.notes && (
                        <p className="text-sm whitespace-pre-line">{feedback.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
              {interview.status === 'scheduled' && (
                <>
                  <Button onClick={() => handleStatusChange('confirmed')} className="gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Confirm Interview
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setRescheduleDialogOpen(true)}
                    className="gap-2"
                  >
                    <Calendar className="h-4 w-4" />
                    Reschedule
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => handleStatusChange('cancelled')}
                    className="gap-2"
                  >
                    <XCircle className="h-4 w-4" />
                    Cancel Interview
                  </Button>
                </>
              )}
              {interview.status === 'confirmed' && !isPast && (
                <>
                  <Button className="gap-2">
                    <Video className="h-4 w-4" />
                    Join Meeting
                  </Button>
                  <Button variant="outline" className="gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Send Message
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setRescheduleDialogOpen(true)}
                    className="gap-2"
                  >
                    <Calendar className="h-4 w-4" />
                    Reschedule
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => handleStatusChange('cancelled')}
                    className="gap-2"
                  >
                    <XCircle className="h-4 w-4" />
                    Cancel Interview
                  </Button>
                </>
              )}
              {interview.status === 'cancelled' && (
                <Button 
                  variant="outline" 
                  onClick={() => setRescheduleDialogOpen(true)}
                  className="gap-2"
                >
                  <Calendar className="h-4 w-4" />
                  Reschedule Interview
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Reschedule Dialog */}
        <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reschedule Interview</DialogTitle>
              <DialogDescription>
                Select a new date and time for your interview.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label htmlFor="date">New Date</label>
                  <Input
                    id="date"
                    type="date"
                    defaultValue={interview.date}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="time">New Time</label>
                  <Input
                    id="time"
                    type="time"
                    defaultValue={interview.time}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRescheduleDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  const newDate = (document.getElementById('date') as HTMLInputElement).value;
                  const newTime = (document.getElementById('time') as HTMLInputElement).value;
                  handleReschedule(newDate, newTime);
                }}
                disabled={isRescheduling}
              >
                {isRescheduling ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                    Rescheduling...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Feedback Dialog */}
        <Dialog open={feedbackDialogOpen} onOpenChange={setFeedbackDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Interview Feedback</DialogTitle>
              <DialogDescription>
                Please provide your feedback about the candidate's interview performance.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Overall Rating (1-10)</label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={feedback.rating}
                  onChange={(e) => setFeedback(prev => ({ 
                    ...prev, 
                    rating: Math.min(10, Math.max(1, parseInt(e.target.value) || 1))
                  }))}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Feedback Notes</label>
                <Textarea
                  value={feedback.notes}
                  onChange={(e) => setFeedback(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Provide detailed feedback about the candidate's performance..."
                  className="min-h-[150px]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Recommendation</label>
                <Textarea
                  value={feedback.recommendation}
                  onChange={(e) => setFeedback(prev => ({ ...prev, recommendation: e.target.value }))}
                  placeholder="Would you recommend moving forward with this candidate? Why or why not?"
                  className="min-h-[100px]"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFeedbackDialogOpen(false)}>
                Save as Draft
              </Button>
              <Button 
                onClick={handleSubmitFeedback}
                disabled={isSavingFeedback || !feedback.rating || !feedback.notes}
              >
                {isSavingFeedback ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                    Submitting...
                  </>
                ) : (
                  'Submit Feedback'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}